import {
  compareDecimal,
  isNewInputRateAmount,
  readMoneyV2,
  readTariffMoneyV2,
  type ConfiguredTariffV2,
  type InvalidTariffV2,
} from "@voltie/localization-web";
import { HttpError } from "@/lib/http";
import { tariffMode, tariffValues } from "@/lib/moneyManagement";
import { UOM_BY_FEE, ruleRate, type PricingFormValues, type PricingRuleValues } from "@/lib/pricingForm";

// The platform-admin switch of a tenant's operating currency (CUR-207 S4b):
// GET/POST /api/org/<id>/operating-currency/switch and POST .../switch/effects,
// contracts/currency/operating_currency_switch.schema.json. Every success body is
// read strictly for the organization it was requested for: closed objects, the
// relationships the schema and its semantic checks (tests/test_currency_contracts.py
// switch_errors) freeze, and each basis tariff through the shared readTariffMoneyV2.
// An answer that breaks the contract is an invalid read, never a basis or a
// confirmation, and nothing unreadable is ever taken as USD. Refusals are classified
// from (status, body) into the frozen codes with only their safe fields; server
// error text is checked and dropped, never kept. target_choices is exactly the
// release's assignable catalog with its exponents; any other stored tariff currency is
// checked only for one exponent across a body (MV-2 registry membership is the server's).
// S4c adds the pure draft, rebase, request builders, unknown-outcome resolution and
// repair-evidence rule at the end of this file.

/** Consumer path ids of the activation scope (currency_activation.schema.json consumer_path). */
export const CONSUMER_PATHS = [
  "unit_assigned_users",
  "admin_unit_and_fleet",
  "org_managers",
  "org_report",
  "public_flow",
  "history_and_receipts",
  "unit_pricing_context",
] as const;
/**
 * The switch target catalog: every assignable operating currency (a registry code with a
 * money policy row) and its currency registry version 1 exponent. A prepare answer offers
 * exactly these; the registry_version it serves is still read, never this constant.
 */
export const SWITCH_TARGET_EXPONENTS: Readonly<Record<string, number>> = Object.freeze({ CAD: 2, EUR: 2, USD: 2 });
/** Recorded consumer states (currency_activation.schema.json consumer_state). */
export const CONSUMER_STATES = ["verified_compatible", "verified_absent", "old_client_affected", "unknown"] as const;

export type ConsumerPath = (typeof CONSUMER_PATHS)[number];
export type ConsumerState = (typeof CONSUMER_STATES)[number];

// ── Wire types of the requests (built by the caller, sent unchanged) ─────────────

export interface SwitchEvidenceRef {
  readonly ref: string;
  readonly recorded_at: string;
}

/** target_activation_scope: the complete EUR/CAD scope input; null for a USD target. */
export interface SwitchActivationScope {
  readonly consumer_states: Readonly<Partial<Record<ConsumerPath, ConsumerState>>>;
  readonly evidence: Readonly<Partial<Record<ConsumerPath, SwitchEvidenceRef>>>;
  readonly suspended: boolean;
}

type ReplacementFeePair =
  | { readonly fee_type: "charging"; readonly uom: "kwh" | "hour" }
  | { readonly fee_type: "idle"; readonly uom: "hour" }
  | { readonly fee_type: "activation"; readonly uom: "session" };

/** replacement_rule: new operator input, rate_base an exact decimal string. */
export type SwitchReplacementRule = ReplacementFeePair & {
  readonly rate_base: string;
  readonly is_tou: boolean;
  readonly start_time: string;
  readonly end_time: string;
  readonly days: string;
  readonly priority: number;
  readonly precision_origin: "new_input";
};

export interface SwitchReplacementPricebook {
  readonly money_version: 2;
  readonly currency_code: string;
  /** The prepared switch_basis.registry_version, never a client constant. */
  readonly registry_version: number;
  readonly effective_pricebook: readonly SwitchReplacementRule[];
}

export interface SwitchReplacementUnit {
  readonly unit_id: string;
  readonly expected_pricebook_version: string;
  readonly pricebook: SwitchReplacementPricebook;
}

export interface SwitchReplacementProfile {
  readonly profile_id: number;
  readonly expected_revision: number;
  readonly pricebook: SwitchReplacementPricebook;
}

/** switch_request: exactly these keys. */
export interface OperatingCurrencySwitchRequest {
  readonly target_currency: string;
  readonly expected_operating_currency: string | null;
  readonly expected_operating_currency_revision: number;
  readonly expected_activation_scope_revision: number | null;
  readonly activation_scope: SwitchActivationScope | null;
  readonly units: readonly SwitchReplacementUnit[];
  readonly profiles: readonly SwitchReplacementProfile[];
}

// ── Read success bodies ──────────────────────────────────────────────────────────

/** A basis item's stored tariff: never unset or unavailable. */
export type SwitchStoredTariff = ConfiguredTariffV2 | InvalidTariffV2;

export type SwitchTargetChoice =
  | SwitchTargetChoiceOf<"eligible", null>
  | SwitchTargetChoiceOf<"ineligible", "same_currency">
  | SwitchTargetChoiceOf<"activation_blocked", "financial_readiness">;

interface SwitchTargetChoiceOf<V, R> {
  readonly currency: string;
  readonly exponent: number;
  /** not_applicable exactly for USD. */
  readonly activation_scope_input: "required" | "not_applicable";
  readonly verdict: V;
  readonly reason: R;
}

export interface SwitchBasisUnit {
  readonly unit_id: string;
  /** Shown for review only; never changed by the switch. */
  readonly pricing_mode: string | null;
  readonly pricebook_version: string;
  readonly tariff: SwitchStoredTariff;
}

export interface SwitchBasisProfile {
  readonly profile_id: number;
  readonly name: string;
  readonly revision: number;
  readonly pricebook_version: string;
  readonly tariff: SwitchStoredTariff;
}

/** switch_basis: the prepare answer; not a durable price or authorization. */
export interface OperatingCurrencySwitchBasis {
  readonly organization_id: number;
  /** The stored code; null when never assigned (effective USD, revision 0). */
  readonly operating_currency: string | null;
  readonly effective_currency: string;
  readonly operating_currency_revision: number;
  readonly activation_scope_revision: number | null;
  /** Ascending by currency. */
  readonly target_choices: readonly SwitchTargetChoice[];
  readonly max_items: number;
  /** The value every replacement pricebook must carry. */
  readonly registry_version: number;
  /** Ascending by unit_id in code-point order. */
  readonly units: readonly SwitchBasisUnit[];
  /** Ascending by profile_id. */
  readonly profiles: readonly SwitchBasisProfile[];
}

export type SwitchEffectOutcome = "applied" | "failed";

export interface SwitchUnitProjection {
  readonly unit_id: string;
  readonly mirror: SwitchEffectOutcome;
  readonly published: SwitchEffectOutcome;
}

/** projection_report: incomplete is still committed and is completed by repair, never by another activation. */
export interface SwitchProjectionReport {
  readonly status: "complete" | "incomplete";
  readonly units: readonly SwitchUnitProjection[];
  readonly response_cache: SwitchEffectOutcome;
  readonly public_pricing_cache: SwitchEffectOutcome;
  readonly re_evaluation: { readonly requested: number; readonly failed: number };
}

export interface SwitchCommittedUnit {
  readonly unit_id: string;
  readonly pricebook_version: string;
}

export interface SwitchCommittedProfile {
  readonly profile_id: number;
  readonly revision: number;
  readonly pricebook_version: string;
}

/** switch_committed: PostgreSQL committed exactly the request it is read against. */
export interface OperatingCurrencySwitchCommitted {
  readonly organization_id: number;
  readonly write_state: "stored";
  readonly operating_currency: string;
  readonly previous_operating_currency: string | null;
  readonly operating_currency_revision: number;
  readonly activation_scope_revision: number | null;
  readonly units: readonly SwitchCommittedUnit[];
  readonly profiles: readonly SwitchCommittedProfile[];
  readonly projection: SwitchProjectionReport;
}

/** switch_repaired: effects re-applied for the requested revision; nothing written. */
export interface OperatingCurrencySwitchRepaired {
  readonly organization_id: number;
  readonly write_state: "not_written";
  readonly operating_currency: string;
  readonly operating_currency_revision: number;
  readonly units: readonly SwitchCommittedUnit[];
  readonly projection: SwitchProjectionReport;
}

export type SwitchReadErrorCode =
  | "not_object"
  | "not_array"
  | "missing_field"
  | "forbidden_key"
  | "field"
  | "currency"
  | "exponent"
  | "exponent_mismatch"
  | "organization_mismatch"
  | "operating_currency"
  | "revision"
  | "target_choice"
  | "order"
  | "max_items"
  | "tariff"
  | "tariff_state"
  | "projection_status"
  | "projection_identity"
  | "request_mismatch";

export type SwitchRead<T> =
  | { readonly status: "value"; readonly value: T }
  | { readonly status: "invalid"; readonly error: SwitchReadErrorCode; readonly path: string; readonly detail: string };

class Invalid extends Error {
  readonly error: SwitchReadErrorCode;
  readonly path: string;
  readonly detail: string;

  constructor(error: SwitchReadErrorCode, path: string, detail: string) {
    super(`${path}: ${error}: ${detail}`);
    this.error = error;
    this.path = path;
    this.detail = detail;
  }
}

const show = (value: unknown) => (typeof value === "string" ? JSON.stringify(value) : String(value));
const join = (path: string, key: string) => (path ? `${path}.${key}` : key);
const PRICEBOOK_VERSION = /^[0-9a-f]{16}$/;

const BASIS_KEYS = ["organization_id", "operating_currency", "effective_currency", "operating_currency_revision", "activation_scope_revision", "target_choices", "max_items", "registry_version", "units", "profiles"];
const CHOICE_KEYS = ["currency", "exponent", "activation_scope_input", "verdict", "reason"];
const BASIS_UNIT_KEYS = ["unit_id", "pricing_mode", "pricebook_version", "tariff"];
const BASIS_PROFILE_KEYS = ["profile_id", "name", "revision", "pricebook_version", "tariff"];
const TARIFF_KEYS = ["tariff_state", "currency", "exponent", "stored_money_version", "rules", "reason"];
const TARIFF_RULE_KEYS = ["index", "fee_type", "uom", "is_tou", "start_time", "end_time", "days", "priority", "rate"];
const RATE_KEYS = ["currency", "amount", "unit", "precision_origin", "money_version"];
const DIAGNOSTIC_RATE_KEYS: Readonly<Record<string, readonly string[]>> = {
  legacy_rate_unrepresentable: ["diagnostic", "raw", "unit", "money_version"],
  rate_unit_unsupported: ["diagnostic", "raw", "money_version"],
};
const COMMITTED_KEYS = ["organization_id", "write_state", "operating_currency", "previous_operating_currency", "operating_currency_revision", "activation_scope_revision", "units", "profiles", "projection"];
const REPAIRED_KEYS = ["organization_id", "write_state", "operating_currency", "operating_currency_revision", "units", "projection"];
const COMMITTED_UNIT_KEYS = ["unit_id", "pricebook_version"];
const COMMITTED_PROFILE_KEYS = ["profile_id", "revision", "pricebook_version"];
const PROJECTION_KEYS = ["status", "units", "response_cache", "public_pricing_cache", "re_evaluation"];
const UNIT_PROJECTION_KEYS = ["unit_id", "mirror", "published"];
const RE_EVALUATION_KEYS = ["requested", "failed"];
const EFFECT_OUTCOMES: readonly SwitchEffectOutcome[] = ["applied", "failed"];

/** A plain JSON object with exactly `keys`: a missing key first, then a key outside them. */
function closed(value: unknown, keys: readonly string[], path: string): Record<string, unknown> {
  const prototype = typeof value === "object" && value !== null && !Array.isArray(value) ? Object.getPrototypeOf(value) : undefined;
  if (prototype !== Object.prototype && prototype !== null) throw new Invalid("not_object", path, show(value));
  const record = value as Record<string, unknown>;
  const missing = keys.find((key) => !Object.hasOwn(record, key));
  if (missing !== undefined) throw new Invalid("missing_field", join(path, missing), missing);
  const extra = Object.keys(record).find((key) => !keys.includes(key));
  if (extra !== undefined) throw new Invalid("forbidden_key", join(path, extra), "not part of the switch contract");
  return record;
}

function array(value: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(value)) throw new Invalid("not_array", path, show(value));
  return value;
}

function integer(value: unknown, min: number, path: string, error: SwitchReadErrorCode = "field"): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < min) throw new Invalid(error, path, show(value));
  return value;
}

function oneOf<T>(value: unknown, allowed: readonly T[], path: string): T {
  if (!allowed.includes(value as T)) throw new Invalid("field", path, show(value));
  return value as T;
}

// A currency and exponent are valid exactly when a zero amount in them is.
function currencyHeader(currency: unknown, exponent: unknown, path: string): { currency: string; exponent: number } {
  const probe = readMoneyV2({ currency, amount_minor: "0", exponent, money_version: 2 });
  if (probe.status === "value") return { currency: probe.value.currency, exponent: probe.value.exponent };
  const exponentError = probe.status === "invalid" && probe.error === "exponent";
  throw new Invalid(exponentError ? "exponent" : "currency", join(path, exponentError ? "exponent" : "currency"), show(exponentError ? exponent : currency));
}

function currencyCode(value: unknown, path: string): string {
  if (typeof value !== "string") throw new Invalid("currency", path, show(value));
  try {
    return currencyHeader(value, 0, "").currency;
  } catch (e) {
    if (e instanceof Invalid) throw new Invalid("currency", path, show(value));
    throw e;
  }
}

function nullableCurrency(value: unknown, path: string): string | null {
  return value === null ? null : currencyCode(value, path);
}

function revisionOrNull(value: unknown, path: string): number | null {
  return value === null ? null : integer(value, 1, path, "revision");
}

function unitId(value: unknown, path: string): string {
  // JSON Schema lengths count code points.
  if (typeof value !== "string" || value.length === 0 || [...value].length > 128) throw new Invalid("field", path, show(value));
  return value;
}

function pricebookVersion(value: unknown, path: string): string {
  if (typeof value !== "string" || !PRICEBOOK_VERSION.test(value)) throw new Invalid("field", path, show(value));
  return value;
}

/** Unicode code-point order, as the server sorts (UTF-16 order differs above the BMP). */
function codePointCompare(a: string, b: string): number {
  const x = [...a];
  const y = [...b];
  for (let i = 0; i < Math.min(x.length, y.length); i++) {
    const d = x[i].codePointAt(0)! - y[i].codePointAt(0)!;
    if (d !== 0) return d;
  }
  return x.length - y.length;
}

/** Strictly ascending, so each identity once. */
function ascending<T>(items: readonly T[], key: (item: T) => string | number, path: string): void {
  for (let i = 1; i < items.length; i++) {
    const a = key(items[i - 1]);
    const b = key(items[i]);
    const order = typeof a === "string" && typeof b === "string" ? codePointCompare(a, b) : (a as number) - (b as number);
    if (!(order < 0)) throw new Invalid("order", `${path}[${i}]`, `${show(b)} after ${show(a)}`);
  }
}

/** One currency has one exponent wherever a body names it. */
function agreeExponent(seen: Map<string, number>, currency: string, exponent: number, path: string): void {
  const known = seen.get(currency);
  if (known !== undefined && known !== exponent) throw new Invalid("exponent_mismatch", path, `${currency} exponent ${exponent} beside ${known}`);
  seen.set(currency, exponent);
}

// The shared reader validates values but ignores unknown keys; the switch tariff is closed.
function closedTariffShape(value: unknown, path: string): void {
  const record = closed(value, TARIFF_KEYS, path);
  if (!Array.isArray(record.rules)) return;
  record.rules.forEach((raw, i) => {
    const at = `${join(path, "rules")}[${i}]`;
    const rule = closed(raw, TARIFF_RULE_KEYS, at);
    const rate = rule.rate;
    const diagnostic = typeof rate === "object" && rate !== null && Object.hasOwn(rate, "diagnostic") ? (rate as Record<string, unknown>).diagnostic : undefined;
    const keys = diagnostic === undefined ? RATE_KEYS : typeof diagnostic === "string" && Object.hasOwn(DIAGNOSTIC_RATE_KEYS, diagnostic) ? DIAGNOSTIC_RATE_KEYS[diagnostic] : undefined;
    if (keys === undefined) throw new Invalid("tariff", join(at, "rate.diagnostic"), show(diagnostic));
    closed(rate, keys, join(at, "rate"));
  });
}

function storedTariff(value: unknown, exponents: Map<string, number>, path: string): SwitchStoredTariff {
  closedTariffShape(value, path);
  const read = readTariffMoneyV2(value);
  if (read.status === "unavailable") throw new Invalid("tariff", path, "no tariff");
  if (read.status === "invalid") throw new Invalid("tariff", read.path ? join(path, read.path) : path, `${read.error}: ${read.detail}`);
  const tariff = read.value;
  if (tariff.tariff_state !== "configured" && tariff.tariff_state !== "invalid") throw new Invalid("tariff_state", join(path, "tariff_state"), tariff.tariff_state);
  if (tariff.tariff_state === "configured") agreeExponent(exponents, tariff.currency, tariff.exponent, join(path, "exponent"));
  return tariff;
}

function targetChoice(value: unknown, effective: string, exponents: Map<string, number>, path: string): SwitchTargetChoice {
  const record = closed(value, CHOICE_KEYS, path);
  const head = currencyHeader(record.currency, record.exponent, path);
  if (!Object.hasOwn(SWITCH_TARGET_EXPONENTS, head.currency)) throw new Invalid("target_choice", join(path, "currency"), `${head.currency} is not assignable`);
  if (head.exponent !== SWITCH_TARGET_EXPONENTS[head.currency])
    throw new Invalid("exponent", join(path, "exponent"), `${head.currency} exponent ${head.exponent}, not ${SWITCH_TARGET_EXPONENTS[head.currency]}`);
  agreeExponent(exponents, head.currency, head.exponent, join(path, "exponent"));
  const activation_scope_input = oneOf(record.activation_scope_input, ["required", "not_applicable"] as const, join(path, "activation_scope_input"));
  // USD is the legacy contour and is never scoped; every other currency is.
  if ((activation_scope_input === "not_applicable") !== (head.currency === "USD"))
    throw new Invalid("target_choice", join(path, "activation_scope_input"), `${activation_scope_input} for ${head.currency}`);
  const verdict = oneOf(record.verdict, ["eligible", "ineligible", "activation_blocked"] as const, join(path, "verdict"));
  const expectedReason = { eligible: null, ineligible: "same_currency", activation_blocked: "financial_readiness" }[verdict];
  if (record.reason !== expectedReason) throw new Invalid("target_choice", join(path, "reason"), `${show(record.reason)} with a ${verdict} verdict`);
  // same_currency is the effective currency's verdict and no other's.
  if ((expectedReason === "same_currency") !== (head.currency === effective))
    throw new Invalid("target_choice", join(path, "verdict"), `${verdict} ${head.currency} with effective currency ${effective}`);
  return { ...head, activation_scope_input, verdict, reason: expectedReason } as SwitchTargetChoice;
}

function basisUnit(value: unknown, exponents: Map<string, number>, path: string): SwitchBasisUnit {
  const record = closed(value, BASIS_UNIT_KEYS, path);
  const pricing_mode = record.pricing_mode;
  if (pricing_mode !== null && typeof pricing_mode !== "string") throw new Invalid("field", join(path, "pricing_mode"), show(pricing_mode));
  return {
    unit_id: unitId(record.unit_id, join(path, "unit_id")),
    pricing_mode,
    pricebook_version: pricebookVersion(record.pricebook_version, join(path, "pricebook_version")),
    tariff: storedTariff(record.tariff, exponents, join(path, "tariff")),
  };
}

function basisProfile(value: unknown, exponents: Map<string, number>, path: string): SwitchBasisProfile {
  const record = closed(value, BASIS_PROFILE_KEYS, path);
  if (typeof record.name !== "string") throw new Invalid("field", join(path, "name"), show(record.name));
  return {
    profile_id: integer(record.profile_id, 1, join(path, "profile_id")),
    name: record.name,
    revision: integer(record.revision, 1, join(path, "revision"), "revision"),
    pricebook_version: pricebookVersion(record.pricebook_version, join(path, "pricebook_version")),
    tariff: storedTariff(record.tariff, exponents, join(path, "tariff")),
  };
}

function organization(value: unknown, organizationId: number): number {
  const id = integer(value, 1, "organization_id");
  // An answer about another organization is never shown or confirmed for this one.
  if (id !== organizationId) throw new Invalid("organization_mismatch", "organization_id", `${id} for organization ${organizationId}`);
  return id;
}

function basis(value: unknown, organizationId: number): OperatingCurrencySwitchBasis {
  const record = closed(value, BASIS_KEYS, "");
  const organization_id = organization(record.organization_id, organizationId);
  const operating_currency = nullableCurrency(record.operating_currency, "operating_currency");
  const effective_currency = currencyCode(record.effective_currency, "effective_currency");
  const operating_currency_revision = integer(record.operating_currency_revision, 0, "operating_currency_revision", "revision");
  if (effective_currency !== (operating_currency ?? "USD"))
    throw new Invalid("operating_currency", "effective_currency", `${effective_currency} for stored ${show(operating_currency)}`);
  // Revision 0 exactly while never assigned.
  if ((operating_currency === null) !== (operating_currency_revision === 0))
    throw new Invalid("revision", "operating_currency_revision", `${operating_currency_revision} for stored ${show(operating_currency)}`);
  const activation_scope_revision = revisionOrNull(record.activation_scope_revision, "activation_scope_revision");
  const max_items = integer(record.max_items, 1, "max_items");
  const registry_version = integer(record.registry_version, 1, "registry_version");

  const exponents = new Map<string, number>();
  const target_choices = array(record.target_choices, "target_choices").map((c, i) => targetChoice(c, effective_currency, exponents, `target_choices[${i}]`));
  ascending(target_choices, (c) => c.currency, "target_choices");
  const missing = Object.keys(SWITCH_TARGET_EXPONENTS).filter((code) => !target_choices.some((c) => c.currency === code));
  if (missing.length > 0) throw new Invalid("target_choice", "target_choices", `no choice for ${missing.join(", ")}`);

  const rawUnits = array(record.units, "units");
  const rawProfiles = array(record.profiles, "profiles");
  // The cap first: an answer never lists more than max_items items.
  if (rawUnits.length + rawProfiles.length > max_items)
    throw new Invalid("max_items", "max_items", `${rawUnits.length} units and ${rawProfiles.length} profiles above ${max_items}`);
  const units = rawUnits.map((u, i) => basisUnit(u, exponents, `units[${i}]`));
  ascending(units, (u) => u.unit_id, "units");
  const profiles = rawProfiles.map((p, i) => basisProfile(p, exponents, `profiles[${i}]`));
  ascending(profiles, (p) => p.profile_id, "profiles");

  return { organization_id, operating_currency, effective_currency, operating_currency_revision, activation_scope_revision, target_choices, max_items, registry_version, units, profiles };
}

function committedUnits(value: unknown, path: string): readonly SwitchCommittedUnit[] {
  const units = array(value, path).map((raw, i) => {
    const at = `${path}[${i}]`;
    const record = closed(raw, COMMITTED_UNIT_KEYS, at);
    return { unit_id: unitId(record.unit_id, join(at, "unit_id")), pricebook_version: pricebookVersion(record.pricebook_version, join(at, "pricebook_version")) };
  });
  ascending(units, (u) => u.unit_id, path);
  return units;
}

function committedProfiles(value: unknown, path: string): readonly SwitchCommittedProfile[] {
  const profiles = array(value, path).map((raw, i) => {
    const at = `${path}[${i}]`;
    const record = closed(raw, COMMITTED_PROFILE_KEYS, at);
    return {
      profile_id: integer(record.profile_id, 1, join(at, "profile_id")),
      revision: integer(record.revision, 1, join(at, "revision"), "revision"),
      pricebook_version: pricebookVersion(record.pricebook_version, join(at, "pricebook_version")),
    };
  });
  ascending(profiles, (p) => p.profile_id, path);
  return profiles;
}

/** projection_report, with one entry per committed unit in the same order. */
function projection(value: unknown, units: readonly SwitchCommittedUnit[], path: string): SwitchProjectionReport {
  const record = closed(value, PROJECTION_KEYS, path);
  const status = oneOf(record.status, ["complete", "incomplete"] as const, join(path, "status"));
  const unitsPath = join(path, "units");
  const projected = array(record.units, unitsPath).map((raw, i) => {
    const at = `${unitsPath}[${i}]`;
    const entry = closed(raw, UNIT_PROJECTION_KEYS, at);
    return {
      unit_id: unitId(entry.unit_id, join(at, "unit_id")),
      mirror: oneOf(entry.mirror, EFFECT_OUTCOMES, join(at, "mirror")),
      published: oneOf(entry.published, EFFECT_OUTCOMES, join(at, "published")),
    };
  });
  if (projected.length !== units.length || projected.some((p, i) => p.unit_id !== units[i].unit_id))
    throw new Invalid("projection_identity", unitsPath, `${projected.map((p) => p.unit_id).join(",")} beside units ${units.map((u) => u.unit_id).join(",")}`);
  const response_cache = oneOf(record.response_cache, EFFECT_OUTCOMES, join(path, "response_cache"));
  const public_pricing_cache = oneOf(record.public_pricing_cache, EFFECT_OUTCOMES, join(path, "public_pricing_cache"));
  const evaluationPath = join(path, "re_evaluation");
  const evaluation = closed(record.re_evaluation, RE_EVALUATION_KEYS, evaluationPath);
  const requested = integer(evaluation.requested, 0, join(evaluationPath, "requested"));
  const failed = integer(evaluation.failed, 0, join(evaluationPath, "failed"));
  // At most one re-evaluation per unit, and only a requested one can fail.
  if (!(failed <= requested && requested <= projected.length))
    throw new Invalid("projection_status", evaluationPath, `failed ${failed}, requested ${requested}, ${projected.length} units`);
  const anyFailed = projected.some((p) => p.mirror === "failed" || p.published === "failed") || response_cache === "failed" || public_pricing_cache === "failed" || failed > 0;
  if ((status === "complete") === anyFailed) throw new Invalid("projection_status", join(path, "status"), `${status} with${anyFailed ? "" : "out"} a failed effect`);
  return { status, units: projected, response_cache, public_pricing_cache, re_evaluation: { requested, failed } };
}

const sortedIds = <T>(items: readonly T[], key: (item: T) => string): string[] => items.map(key).sort(codePointCompare);

function committed(value: unknown, organizationId: number, request: OperatingCurrencySwitchRequest): OperatingCurrencySwitchCommitted {
  const record = closed(value, COMMITTED_KEYS, "");
  const organization_id = organization(record.organization_id, organizationId);
  if (record.write_state !== "stored") throw new Invalid("field", "write_state", show(record.write_state));
  const operating_currency = currencyCode(record.operating_currency, "operating_currency");
  const previous_operating_currency = nullableCurrency(record.previous_operating_currency, "previous_operating_currency");
  const operating_currency_revision = integer(record.operating_currency_revision, 1, "operating_currency_revision", "revision");
  const activation_scope_revision = revisionOrNull(record.activation_scope_revision, "activation_scope_revision");
  // A switch changes the effective currency (null was USD); a non-USD target always has a written scope.
  if (operating_currency === (previous_operating_currency ?? "USD"))
    throw new Invalid("operating_currency", "previous_operating_currency", `${show(previous_operating_currency)} switched to ${operating_currency}`);
  if (operating_currency !== "USD" && activation_scope_revision === null)
    throw new Invalid("revision", "activation_scope_revision", `null for ${operating_currency}`);
  const units = committedUnits(record.units, "units");
  const profiles = committedProfiles(record.profiles, "profiles");
  const report = projection(record.projection, units, "projection");

  // Committed is exactly this request: its target over its expected basis, revision + 1,
  // the USD scope revision unchanged or the EUR/CAD scope written once, and every item it replaced.
  const mismatch = (path: string, detail: string) => new Invalid("request_mismatch", path, detail);
  if (operating_currency !== request.target_currency) throw mismatch("operating_currency", `${operating_currency} for target ${request.target_currency}`);
  if (previous_operating_currency !== request.expected_operating_currency)
    throw mismatch("previous_operating_currency", `${show(previous_operating_currency)} for expected ${show(request.expected_operating_currency)}`);
  if (operating_currency_revision !== request.expected_operating_currency_revision + 1)
    throw mismatch("operating_currency_revision", `${operating_currency_revision} for expected ${request.expected_operating_currency_revision}`);
  const scopeRevision = operating_currency === "USD" ? request.expected_activation_scope_revision : (request.expected_activation_scope_revision ?? 0) + 1;
  if (activation_scope_revision !== scopeRevision)
    throw mismatch("activation_scope_revision", `${show(activation_scope_revision)} for expected ${show(request.expected_activation_scope_revision)}`);
  const requestedUnits = sortedIds(request.units, (u) => u.unit_id);
  if (units.length !== requestedUnits.length || units.some((u, i) => u.unit_id !== requestedUnits[i]))
    throw mismatch("units", `${units.length} committed units for ${requestedUnits.length} replaced`);
  const requestedProfiles = [...request.profiles].sort((a, b) => a.profile_id - b.profile_id);
  if (profiles.length !== requestedProfiles.length || profiles.some((p, i) => p.profile_id !== requestedProfiles[i].profile_id || p.revision !== requestedProfiles[i].expected_revision + 1))
    throw mismatch("profiles", `${profiles.length} committed profiles for ${requestedProfiles.length} replaced`);

  return { organization_id, write_state: "stored", operating_currency, previous_operating_currency, operating_currency_revision, activation_scope_revision, units, profiles, projection: report };
}

function repaired(value: unknown, organizationId: number, revision: number): OperatingCurrencySwitchRepaired {
  const record = closed(value, REPAIRED_KEYS, "");
  const organization_id = organization(record.organization_id, organizationId);
  if (record.write_state !== "not_written") throw new Invalid("field", "write_state", show(record.write_state));
  const operating_currency = currencyCode(record.operating_currency, "operating_currency");
  const operating_currency_revision = integer(record.operating_currency_revision, 1, "operating_currency_revision", "revision");
  if (operating_currency_revision !== revision) throw new Invalid("request_mismatch", "operating_currency_revision", `${operating_currency_revision} for requested ${revision}`);
  const units = committedUnits(record.units, "units");
  return { organization_id, write_state: "not_written", operating_currency, operating_currency_revision, units, projection: projection(record.projection, units, "projection") };
}

function deepFreeze<T>(input: T): T {
  if (typeof input === "object" && input !== null && !Object.isFrozen(input)) {
    for (const member of Object.values(input)) deepFreeze(member);
    Object.freeze(input);
  }
  return input;
}

function attempt<T>(read: () => T): SwitchRead<T> {
  try {
    return Object.freeze({ status: "value" as const, value: deepFreeze(read()) });
  } catch (e) {
    if (e instanceof Invalid) return Object.freeze({ status: "invalid" as const, error: e.error, path: e.path, detail: e.detail });
    throw e;
  }
}

/** Reads a 200 prepare body (switch_basis) for the organization it was requested for. */
export function readSwitchBasis(input: unknown, organizationId: number): SwitchRead<OperatingCurrencySwitchBasis> {
  return attempt(() => basis(input, organizationId));
}

/** Reads a 200 activation body (switch_committed) as the commit of exactly `request` for this organization. */
export function readSwitchCommitted(input: unknown, organizationId: number, request: OperatingCurrencySwitchRequest): SwitchRead<OperatingCurrencySwitchCommitted> {
  return attempt(() => committed(input, organizationId, request));
}

/** Reads a 200 repair body (switch_repaired) for this organization and the requested revision. */
export function readSwitchRepaired(input: unknown, organizationId: number, revision: number): SwitchRead<OperatingCurrencySwitchRepaired> {
  return attempt(() => repaired(input, organizationId, revision));
}

// ── Classify refusals ────────────────────────────────────────────────────────────

export type SwitchOperation = "prepare" | "activate" | "repair";

type RefusalDef =
  | "organization_not_found"
  | "operating_currency_unavailable"
  | "operating_currency_not_assignable"
  | "switch_invalid"
  | "switch_same_currency"
  | "switch_stale"
  | "switch_basis_unreadable"
  | "switch_items_changed"
  | "currency_not_enabled"
  | "money_version_2_not_enabled"
  | "switch_too_large"
  | "switch_busy"
  | "switch_commit_unknown"
  | "switch_repair_stale";

/** The refusal bodies each operation answers, per status (x-voltie-routes responses). */
export const SWITCH_REFUSALS: Readonly<Record<SwitchOperation, Readonly<Record<number, readonly RefusalDef[]>>>> = {
  prepare: {
    404: ["organization_not_found"],
    409: ["operating_currency_unavailable", "switch_basis_unreadable"],
    413: ["switch_too_large"],
    503: ["operating_currency_unavailable", "switch_busy"],
  },
  activate: {
    400: ["switch_invalid"],
    404: ["organization_not_found"],
    409: ["operating_currency_not_assignable", "switch_same_currency", "operating_currency_unavailable", "switch_stale", "switch_basis_unreadable", "switch_items_changed", "currency_not_enabled", "money_version_2_not_enabled"],
    413: ["switch_too_large"],
    503: ["operating_currency_unavailable", "switch_busy", "switch_commit_unknown"],
  },
  repair: {
    400: ["switch_invalid"],
    404: ["organization_not_found"],
    409: ["operating_currency_unavailable", "switch_repair_stale"],
    413: ["switch_too_large"],
    503: ["operating_currency_unavailable"],
  },
};

export const SWITCH_INVALID_REASONS = [
  "fields_invalid", "nul_character", "currency_invalid", "currency_not_in_registry", "revision_invalid",
  "activation_scope_missing", "currency_not_scoped", "consumer_states_invalid", "evidence_invalid",
  "suspended_invalid", "consumer_path_unknown", "consumer_state_unknown", "evidence_path_unknown",
  "duplicate_unit", "duplicate_profile", "pricebook_invalid", "pricebook_currency_mismatch",
  "rate_invalid", "paid_requires_positive_rate",
] as const;
export type SwitchInvalidReason = (typeof SWITCH_INVALID_REASONS)[number];
const ITEM_REASONS: readonly SwitchInvalidReason[] = ["duplicate_unit", "duplicate_profile", "pricebook_invalid", "pricebook_currency_mismatch", "rate_invalid", "paid_requires_positive_rate"];
/** The closed repair body can only fail these (repair evaluation order). */
const REPAIR_INVALID_REASONS: readonly SwitchInvalidReason[] = ["fields_invalid", "nul_character", "revision_invalid"];

export interface SwitchItemCounts {
  readonly missing: number;
  readonly unexpected: number;
  readonly stale: number;
}

/** A refusal with only its frozen safe fields; never the server's error text. */
export type SwitchRefusal =
  | { readonly code: "organization_not_found"; readonly status: 404 }
  | { readonly code: "operating_currency_unavailable"; readonly status: 409 | 503 }
  | { readonly code: "operating_currency_not_assignable"; readonly status: 409; readonly currency: string }
  | {
    readonly code: "operating_currency_switch_invalid";
    readonly status: 400;
    readonly reason: SwitchInvalidReason;
    /** The submitted element by kind and array index; no submitted value. */
    readonly item: { readonly kind: "unit" | "profile"; readonly index: number } | null;
  }
  | { readonly code: "operating_currency_switch_same_currency"; readonly status: 409; readonly effective_currency: string }
  | {
    readonly code: "operating_currency_switch_stale";
    readonly status: 409;
    readonly reason: "operating_currency_changed" | "operating_currency_revision_changed" | "activation_scope_revision_changed";
    readonly operating_currency: string | null;
    readonly operating_currency_revision: number;
    readonly activation_scope_revision: number | null;
  }
  | { readonly code: "operating_currency_switch_basis_unreadable"; readonly status: 409; readonly units: number; readonly profiles: number }
  | {
    readonly code: "operating_currency_switch_items_changed";
    readonly status: 409;
    readonly reason: "item_set_changed" | "item_version_changed";
    readonly units: SwitchItemCounts;
    readonly profiles: SwitchItemCounts;
  }
  | {
    readonly code: "currency_not_enabled";
    readonly status: 409;
    readonly currency: string;
    readonly verdict: "ineligible" | "activation_blocked";
    readonly reason: "known_old_client_would_fail" | "unknown_consumer" | "financial_readiness";
  }
  | { readonly code: "money_version_2_not_enabled"; readonly status: 409; readonly reason: "legacy_projection_pending" }
  | { readonly code: "operating_currency_switch_too_large"; readonly status: 413; readonly limit: number; readonly item_count: number }
  /** A 413 without the switch body (a proxy limit): nothing reached the switch. */
  | { readonly code: "request_too_large"; readonly status: 413 }
  | { readonly code: "operating_currency_switch_busy"; readonly status: 503; readonly write_state: "not_written" }
  /** COMMIT was sent and its outcome is unknown: re-read prepare, never replay. */
  | { readonly code: "operating_currency_switch_commit_unknown"; readonly status: 503; readonly write_state: "unknown" }
  | { readonly code: "operating_currency_switch_repair_stale"; readonly status: 409; readonly reason: "superseded" | "not_committed"; readonly operating_currency_revision: number }
  /** The existing auth refusals, shown generically. */
  | { readonly code: "access_denied"; readonly status: 401 | 403 }
  /** Anything else: another status or body, a broken frozen body, a 5xx, no response or a non-HTTP failure. */
  | { readonly code: "unknown" };

const UNKNOWN: SwitchRefusal = Object.freeze({ code: "unknown" });

function errorText(record: Record<string, unknown>, exact?: string): void {
  const error = record.error;
  if (typeof error !== "string" || error === "" || (exact !== undefined && error !== exact)) throw new Invalid("field", "error", "error text");
}

function codeBody(body: unknown, keys: readonly string[], code: string): Record<string, unknown> {
  const record = closed(body, ["error", "code", ...keys], "");
  if (record.code !== code) throw new Invalid("field", "code", show(record.code));
  errorText(record);
  return record;
}

function itemCounts(value: unknown, path: string): SwitchItemCounts {
  const record = closed(value, ["missing", "unexpected", "stale"], path);
  return {
    missing: integer(record.missing, 0, join(path, "missing")),
    unexpected: integer(record.unexpected, 0, join(path, "unexpected")),
    stale: integer(record.stale, 0, join(path, "stale")),
  };
}

// item names a submitted array element: always for the item reasons; for fields_invalid
// only when one activation element is malformed (the repair body has no array); else null.
function invalidItem(value: unknown, reason: SwitchInvalidReason, operation: SwitchOperation): { kind: "unit" | "profile"; index: number } | null {
  const elementFieldsInvalid = reason === "fields_invalid" && operation === "activate";
  if (ITEM_REASONS.includes(reason) ? value === null : value !== null && !elementFieldsInvalid) throw new Invalid("field", "item", `${show(value)} for ${reason}`);
  if (value === null) return null;
  const record = closed(value, ["kind", "index"], "item");
  const kind = oneOf(record.kind, ["unit", "profile"] as const, "item.kind");
  const index = integer(record.index, 0, "item.index");
  const required = reason === "duplicate_unit" || reason === "paid_requires_positive_rate" ? "unit" : reason === "duplicate_profile" ? "profile" : kind;
  if (kind !== required) throw new Invalid("field", "item.kind", `${kind} for ${reason}`);
  return { kind, index };
}

function refusal(def: RefusalDef, status: number, body: unknown, operation: SwitchOperation): SwitchRefusal {
  switch (def) {
    case "organization_not_found":
      errorText(closed(body, ["error"], ""), "Organization not found");
      return { code: "organization_not_found", status: 404 };
    case "operating_currency_unavailable": {
      const record = codeBody(body, [], "operating_currency_unavailable");
      errorText(record, "the organization operating currency is unavailable");
      return { code: "operating_currency_unavailable", status: status as 409 | 503 };
    }
    case "operating_currency_not_assignable": {
      const record = codeBody(body, ["currency"], "operating_currency_not_assignable");
      return { code: "operating_currency_not_assignable", status: 409, currency: currencyCode(record.currency, "currency") };
    }
    case "switch_invalid": {
      const record = codeBody(body, ["reason", "item"], "operating_currency_switch_invalid");
      const reason = oneOf(record.reason, operation === "repair" ? REPAIR_INVALID_REASONS : SWITCH_INVALID_REASONS, "reason");
      return { code: "operating_currency_switch_invalid", status: 400, reason, item: invalidItem(record.item, reason, operation) };
    }
    case "switch_same_currency": {
      const record = codeBody(body, ["effective_currency"], "operating_currency_switch_same_currency");
      return { code: "operating_currency_switch_same_currency", status: 409, effective_currency: currencyCode(record.effective_currency, "effective_currency") };
    }
    case "switch_stale": {
      const record = codeBody(body, ["reason", "operating_currency", "operating_currency_revision", "activation_scope_revision"], "operating_currency_switch_stale");
      const reason = oneOf(record.reason, ["operating_currency_changed", "operating_currency_revision_changed", "activation_scope_revision_changed"] as const, "reason");
      const operating_currency = nullableCurrency(record.operating_currency, "operating_currency");
      const operating_currency_revision = integer(record.operating_currency_revision, 0, "operating_currency_revision", "revision");
      if ((operating_currency === null) !== (operating_currency_revision === 0)) throw new Invalid("revision", "operating_currency_revision", show(operating_currency_revision));
      return { code: "operating_currency_switch_stale", status: 409, reason, operating_currency, operating_currency_revision, activation_scope_revision: revisionOrNull(record.activation_scope_revision, "activation_scope_revision") };
    }
    case "switch_basis_unreadable": {
      const record = codeBody(body, ["units", "profiles"], "operating_currency_switch_basis_unreadable");
      return { code: "operating_currency_switch_basis_unreadable", status: 409, units: integer(record.units, 0, "units"), profiles: integer(record.profiles, 0, "profiles") };
    }
    case "switch_items_changed": {
      const record = codeBody(body, ["reason", "units", "profiles"], "operating_currency_switch_items_changed");
      const reason = oneOf(record.reason, ["item_set_changed", "item_version_changed"] as const, "reason");
      const units = itemCounts(record.units, "units");
      const profiles = itemCounts(record.profiles, "profiles");
      // Sets first: a version change is reported only when both sets match.
      const setChanged = units.missing + units.unexpected + profiles.missing + profiles.unexpected > 0;
      if (reason === "item_set_changed" ? !setChanged : setChanged || units.stale + profiles.stale === 0) throw new Invalid("field", "reason", reason);
      return { code: "operating_currency_switch_items_changed", status: 409, reason, units, profiles };
    }
    case "currency_not_enabled": {
      const record = codeBody(body, ["currency", "verdict", "reason"], "currency_not_enabled");
      const reason = oneOf(record.reason, ["known_old_client_would_fail", "unknown_consumer", "financial_readiness"] as const, "reason");
      const verdict = reason === "known_old_client_would_fail" ? "ineligible" : "activation_blocked";
      if (record.verdict !== verdict) throw new Invalid("field", "verdict", show(record.verdict));
      return { code: "currency_not_enabled", status: 409, currency: currencyCode(record.currency, "currency"), verdict, reason };
    }
    case "money_version_2_not_enabled": {
      const record = codeBody(body, ["reason"], "money_version_2_not_enabled");
      errorText(record, "money version 2 tariffs are not enabled yet");
      if (record.reason !== "legacy_projection_pending") throw new Invalid("field", "reason", show(record.reason));
      return { code: "money_version_2_not_enabled", status: 409, reason: "legacy_projection_pending" };
    }
    case "switch_too_large": {
      const record = codeBody(body, ["limit", "item_count"], "operating_currency_switch_too_large");
      const limit = integer(record.limit, 1, "limit");
      const item_count = integer(record.item_count, 2, "item_count");
      if (item_count <= limit) throw new Invalid("max_items", "item_count", `${item_count} within ${limit}`);
      return { code: "operating_currency_switch_too_large", status: 413, limit, item_count };
    }
    case "switch_busy": {
      const record = codeBody(body, ["write_state"], "operating_currency_switch_busy");
      if (record.write_state !== "not_written") throw new Invalid("field", "write_state", show(record.write_state));
      return { code: "operating_currency_switch_busy", status: 503, write_state: "not_written" };
    }
    case "switch_commit_unknown": {
      const record = codeBody(body, ["write_state"], "operating_currency_switch_commit_unknown");
      if (record.write_state !== "unknown") throw new Invalid("field", "write_state", show(record.write_state));
      return { code: "operating_currency_switch_commit_unknown", status: 503, write_state: "unknown" };
    }
    case "switch_repair_stale": {
      const record = codeBody(body, ["reason", "operating_currency_revision"], "operating_currency_switch_repair_stale");
      const reason = oneOf(record.reason, ["superseded", "not_committed"] as const, "reason");
      return { code: "operating_currency_switch_repair_stale", status: 409, reason, operating_currency_revision: integer(record.operating_currency_revision, 0, "operating_currency_revision", "revision") };
    }
  }
}

/** The body's own code, or the organization route's code-less 404. */
const BODY_CODES: Readonly<Record<RefusalDef, string | undefined>> = {
  organization_not_found: undefined,
  operating_currency_unavailable: "operating_currency_unavailable",
  operating_currency_not_assignable: "operating_currency_not_assignable",
  switch_invalid: "operating_currency_switch_invalid",
  switch_same_currency: "operating_currency_switch_same_currency",
  switch_stale: "operating_currency_switch_stale",
  switch_basis_unreadable: "operating_currency_switch_basis_unreadable",
  switch_items_changed: "operating_currency_switch_items_changed",
  currency_not_enabled: "currency_not_enabled",
  money_version_2_not_enabled: "money_version_2_not_enabled",
  switch_too_large: "operating_currency_switch_too_large",
  switch_busy: "operating_currency_switch_busy",
  switch_commit_unknown: "operating_currency_switch_commit_unknown",
  switch_repair_stale: "operating_currency_switch_repair_stale",
};

/**
 * Classifies a failed prepare, activation or repair into its frozen refusal. Only an
 * HttpError with a status and exact body this operation answers is a refusal; a frozen
 * code with a broken body, any other answer, a 5xx outside the contract, no response or a
 * non-HTTP failure is unknown. A 413 without the switch code is a proxy limit.
 */
export function classifySwitchRefusal(error: unknown, operation: SwitchOperation): SwitchRefusal {
  if (!(error instanceof HttpError)) return UNKNOWN;
  const { status, body } = error;
  if (status === 401 || status === 403) return Object.freeze({ code: "access_denied", status });
  const code = typeof body === "object" && body !== null && !Array.isArray(body) ? (body as Record<string, unknown>).code : undefined;
  if (status === 413 && code !== BODY_CODES.switch_too_large) return Object.freeze({ code: "request_too_large", status: 413 });
  const def = (SWITCH_REFUSALS[operation][status] ?? []).find((d) => BODY_CODES[d] === code);
  if (def === undefined) return UNKNOWN;
  try {
    return deepFreeze(refusal(def, status, body, operation));
  } catch (e) {
    if (e instanceof Invalid) return UNKNOWN;
    throw e;
  }
}

// ── Draft, rebase and request builder (S4c) ──────────────────────────────────────
//
// One immutable draft per accepted switch_basis: the target, its activation-scope input,
// one entry per basis unit and enabled profile, and the confirmation. Every operation is
// pure and returns a new frozen draft (or a closed error); the draft holds kinds,
// parameters and typed values only, never text, and lives in React state alone.
// An entry is in the target currency or does not exist: changing the target clears the
// entries and its scope instead of relabelling them. Nothing is converted: a copy keeps
// every digit, drops the saved provenance and becomes new input in the target currency.

/** Per-rule caps of new tariff input in major units (operating_currency_switch.schema.json x-voltie-rate-caps). */
export const SWITCH_RATE_CAPS: Readonly<Record<string, string>> = Object.freeze({
  "charging|kwh": "10",
  "charging|hour": "50",
  "idle|hour": "50",
  "activation|session": "100",
});

export type SwitchItemRef =
  | { readonly kind: "unit"; readonly unit_id: string }
  | { readonly kind: "profile"; readonly profile_id: number };

/** How the values of an entry were made: typed, copied from the current tariff in `from`, or explicitly no charge. */
export type SwitchEntrySource =
  | { readonly kind: "entry" }
  | { readonly kind: "copy"; readonly from: string }
  | { readonly kind: "no_charge" };

/**
 * not_entered: nothing typed yet. entered: explicitly entered for this basis version.
 * changed: the item changed on the server after it was entered; the typed values are
 * kept but must be entered again explicitly.
 */
export type SwitchEntryStatus = "not_entered" | "entered" | "changed";

export type SwitchDraftEntry =
  | { readonly status: "not_entered"; readonly source: null; readonly values: null }
  | { readonly status: "entered" | "changed"; readonly source: SwitchEntrySource; readonly values: PricingFormValues };

export interface SwitchDraftUnit {
  readonly item: SwitchBasisUnit;
  readonly entry: SwitchDraftEntry;
}

export interface SwitchDraftProfile {
  readonly item: SwitchBasisProfile;
  readonly entry: SwitchDraftEntry;
}

export interface SwitchScopeEvidenceInput {
  readonly ref: string;
  readonly recorded_at: string;
}

/** The EUR/CAD activation-scope input: every path starts unknown, nothing is prefilled from a stored scope. */
export interface SwitchScopeDraft {
  readonly consumer_states: Readonly<Record<ConsumerPath, ConsumerState>>;
  readonly evidence: Readonly<Record<ConsumerPath, SwitchScopeEvidenceInput>>;
  readonly suspended: boolean;
}

export interface OperatingCurrencySwitchDraft {
  /** The accepted basis: organization, currency, revisions, registry version and every item version. */
  readonly basis: OperatingCurrencySwitchBasis;
  /** An eligible target choice of the basis, or null before one is selected. */
  readonly target: string | null;
  /** Present exactly for a selected target whose scope input is required. */
  readonly scope: SwitchScopeDraft | null;
  /** In basis order. */
  readonly units: readonly SwitchDraftUnit[];
  readonly profiles: readonly SwitchDraftProfile[];
  readonly confirmed: boolean;
}

export type SwitchDraftErrorCode =
  | "target_not_eligible"
  | "no_target"
  | "scope_not_applicable"
  | "consumer_path_unknown"
  | "consumer_state_unknown"
  | "unknown_item"
  | "entry_currency"
  | "not_copyable"
  | "not_ready"
  | "organization_mismatch"
  | "older_basis";

export type SwitchDraftResult =
  | { readonly ok: true; readonly draft: OperatingCurrencySwitchDraft }
  | { readonly ok: false; readonly error: SwitchDraftErrorCode };

/** Why a rule cannot be written as new input. */
export type SwitchRuleProblem =
  | "fee_uom"
  | "rate_invalid"
  | "rate_negative"
  | "rate_not_new_input"
  | "rate_over_cap"
  | "window_invalid"
  | "window_empty"
  | "days_invalid"
  | "priority_invalid"
  | "all_day_conflict";

/** Something that keeps the draft from a request; `rule` is the rule's position in the entry. */
export type SwitchDraftProblem =
  | { readonly kind: "target_missing" }
  | { readonly kind: "evidence_incomplete"; readonly path: ConsumerPath }
  /** The server refuses a NUL anywhere in the request (nul_character) before reading it. */
  | { readonly kind: "evidence_nul_character"; readonly path: ConsumerPath; readonly field: "ref" | "recorded_at" }
  | { readonly kind: "item_not_entered"; readonly item: SwitchItemRef }
  | { readonly kind: "item_changed"; readonly item: SwitchItemRef }
  | { readonly kind: "rule_invalid"; readonly item: SwitchItemRef; readonly rule: number; readonly problem: SwitchRuleProblem }
  | { readonly kind: "paid_requires_positive_rate"; readonly item: SwitchItemRef };

export type SwitchBuildError = SwitchDraftProblem | { readonly kind: "not_confirmed" };

export type SwitchBuildResult =
  | { readonly ok: true; readonly request: OperatingCurrencySwitchRequest }
  | { readonly ok: false; readonly errors: readonly SwitchBuildError[] };

const NOT_ENTERED: SwitchDraftEntry = Object.freeze({ status: "not_entered", source: null, values: null });
const HHMM = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
const DAYS = /^$|^[0-6](,[0-6])*$/;

const ok = (draft: OperatingCurrencySwitchDraft): SwitchDraftResult => Object.freeze({ ok: true as const, draft: deepFreeze(draft) });
const fail = (error: SwitchDraftErrorCode): SwitchDraftResult => Object.freeze({ ok: false as const, error });

const unitRef = (unit: SwitchBasisUnit): SwitchItemRef => ({ kind: "unit", unit_id: unit.unit_id });
const profileRef = (profile: SwitchBasisProfile): SwitchItemRef => ({ kind: "profile", profile_id: profile.profile_id });

function eligibleTarget(basis: OperatingCurrencySwitchBasis, currency: string): SwitchTargetChoice | undefined {
  return basis.target_choices.find((c) => c.currency === currency && c.verdict === "eligible");
}

function initialScope(): SwitchScopeDraft {
  const each = <T>(value: () => T) => Object.fromEntries(CONSUMER_PATHS.map((p) => [p, value()])) as Record<ConsumerPath, T>;
  return { consumer_states: each(() => "unknown" as ConsumerState), evidence: each(() => ({ ref: "", recorded_at: "" })), suspended: false };
}

/** A new draft for an accepted basis: no target, no entries, not confirmed. */
export function createSwitchDraft(basis: OperatingCurrencySwitchBasis): OperatingCurrencySwitchDraft {
  return deepFreeze({
    basis,
    target: null,
    scope: null,
    units: basis.units.map((item) => ({ item, entry: NOT_ENTERED })),
    profiles: basis.profiles.map((item) => ({ item, entry: NOT_ENTERED })),
    confirmed: false,
  });
}

const cleared = (draft: OperatingCurrencySwitchDraft) => ({
  units: draft.units.map(({ item }) => ({ item, entry: NOT_ENTERED })),
  profiles: draft.profiles.map(({ item }) => ({ item, entry: NOT_ENTERED })),
});

/**
 * Selects an eligible target choice of the basis. Selecting the current target keeps the
 * entries and scope; another target clears both, since entries are prices in the target.
 */
export function selectSwitchTarget(draft: OperatingCurrencySwitchDraft, currency: string): SwitchDraftResult {
  const choice = eligibleTarget(draft.basis, currency);
  if (choice === undefined) return fail("target_not_eligible");
  if (draft.target === currency) return ok({ ...draft, confirmed: false });
  return ok({ ...draft, ...cleared(draft), target: currency, scope: choice.activation_scope_input === "required" ? initialScope() : null, confirmed: false });
}

function withScope(draft: OperatingCurrencySwitchDraft, path: string, edit: (scope: SwitchScopeDraft, path: ConsumerPath) => SwitchScopeDraft | SwitchDraftErrorCode): SwitchDraftResult {
  if (draft.target === null) return fail("no_target");
  if (draft.scope === null) return fail("scope_not_applicable");
  if (!(CONSUMER_PATHS as readonly string[]).includes(path)) return fail("consumer_path_unknown");
  const scope = edit(draft.scope, path as ConsumerPath);
  return typeof scope === "string" ? fail(scope) : ok({ ...draft, scope, confirmed: false });
}

export function setSwitchConsumerState(draft: OperatingCurrencySwitchDraft, path: string, state: string): SwitchDraftResult {
  return withScope(draft, path, (scope, p) =>
    (CONSUMER_STATES as readonly string[]).includes(state) ? { ...scope, consumer_states: { ...scope.consumer_states, [p]: state as ConsumerState } } : "consumer_state_unknown");
}

/** The optional review reference and recorded date of a path, as typed; sent only when both are given. */
export function setSwitchScopeEvidence(draft: OperatingCurrencySwitchDraft, path: string, evidence: SwitchScopeEvidenceInput): SwitchDraftResult {
  return withScope(draft, path, (scope, p) => ({ ...scope, evidence: { ...scope.evidence, [p]: { ref: evidence.ref, recorded_at: evidence.recorded_at } } }));
}

export function setSwitchScopeSuspended(draft: OperatingCurrencySwitchDraft, suspended: boolean): SwitchDraftResult {
  if (draft.target === null) return fail("no_target");
  if (draft.scope === null) return fail("scope_not_applicable");
  return ok({ ...draft, scope: { ...draft.scope, suspended }, confirmed: false });
}

const sameRef = (a: SwitchItemRef, b: SwitchItemRef) =>
  a.kind === "unit" ? b.kind === "unit" && a.unit_id === b.unit_id : b.kind === "profile" && a.profile_id === b.profile_id;

function withEntry(draft: OperatingCurrencySwitchDraft, ref: SwitchItemRef, make: (tariff: SwitchStoredTariff) => SwitchDraftEntry | SwitchDraftErrorCode): SwitchDraftResult {
  if (draft.target === null) return fail("no_target");
  let found = false;
  let error: SwitchDraftErrorCode | null = null;
  const update = <T extends SwitchDraftUnit | SwitchDraftProfile>(slot: T, own: SwitchItemRef): T => {
    if (!sameRef(own, ref)) return slot;
    found = true;
    const entry = make(slot.item.tariff);
    if (typeof entry === "string") {
      error = entry;
      return slot;
    }
    return { ...slot, entry };
  };
  const units = draft.units.map((u) => update(u, unitRef(u.item)));
  const profiles = draft.profiles.map((p) => update(p, profileRef(p.item)));
  if (!found) return fail("unknown_item");
  if (error !== null) return fail(error);
  return ok({ ...draft, units, profiles, confirmed: false });
}

// Rules keep their typed fields only: the saved provenance of an earlier tariff never travels into the target.
const newInputRules = (rules: readonly PricingRuleValues[]): PricingRuleValues[] =>
  rules.map(({ saved: _saved, ...rule }) => ({ ...rule }));

/** Explicit entry of an item's prices, in the target currency (as PricingForm edits them with the target). */
export function enterSwitchItem(draft: OperatingCurrencySwitchDraft, ref: SwitchItemRef, values: PricingFormValues): SwitchDraftResult {
  if (draft.target !== null && values.currency !== draft.target) return fail("entry_currency");
  return withEntry(draft, ref, () => ({ status: "entered", source: { kind: "entry" }, values: { currency: values.currency, rules: newInputRules(values.rules) } }));
}

/**
 * Explicit copy of the item's readable current rules into the target currency: the same
 * digits as new input, no conversion. An invalid tariff or one with an unreadable rate
 * cannot be copied.
 */
export function copySwitchItemRules(draft: OperatingCurrencySwitchDraft, ref: SwitchItemRef): SwitchDraftResult {
  const target = draft.target;
  return withEntry(draft, ref, (tariff) => copiedEntry(tariff, target));
}

function copiedEntry(tariff: SwitchStoredTariff, target: string | null): SwitchDraftEntry | SwitchDraftErrorCode {
  const values = tariffMode(tariff) === null ? null : tariffValues(tariff);
  if (values === null || !values.ok || tariff.tariff_state !== "configured" || target === null) return "not_copyable";
  return { status: "entered", source: { kind: "copy", from: tariff.currency }, values: { currency: target, rules: newInputRules(values.value.rules) } };
}

/**
 * Copies the current rules into every item whose tariff can be copied, replacing what was
 * entered there; `skipped` lists the items that could not be copied and keep their entry.
 */
export function copyAllSwitchItemRules(draft: OperatingCurrencySwitchDraft): SwitchDraftResult & { readonly skipped?: readonly SwitchItemRef[] } {
  if (draft.target === null) return fail("no_target");
  const skipped: SwitchItemRef[] = [];
  const copy = <T extends SwitchDraftUnit | SwitchDraftProfile>(slot: T, ref: SwitchItemRef): T => {
    const entry = copiedEntry(slot.item.tariff, draft.target);
    if (typeof entry !== "string") return { ...slot, entry };
    skipped.push(ref);
    return slot;
  };
  const units = draft.units.map((u) => copy(u, unitRef(u.item)));
  const profiles = draft.profiles.map((p) => copy(p, profileRef(p.item)));
  return Object.freeze({ ...ok({ ...draft, units, profiles, confirmed: false }), skipped: deepFreeze(skipped) });
}

/** Explicit no-charge rules: an empty target pricebook. A paid unit still needs a positive rate. */
export function noChargeSwitchItem(draft: OperatingCurrencySwitchDraft, ref: SwitchItemRef): SwitchDraftResult {
  const target = draft.target;
  return withEntry(draft, ref, () => (target === null ? "no_target" : { status: "entered", source: { kind: "no_charge" }, values: { currency: target, rules: [] } }));
}

/** Confirmation is possible only for a draft without problems; any later change resets it. */
export function setSwitchConfirmation(draft: OperatingCurrencySwitchDraft, confirmed: boolean): SwitchDraftResult {
  if (confirmed && switchDraftProblems(draft).length > 0) return fail("not_ready");
  return ok({ ...draft, confirmed });
}

function ruleProblem(rule: PricingRuleValues, rate: ReturnType<typeof ruleRate>): SwitchRuleProblem | null {
  if (!UOM_BY_FEE[rule.fee_type]?.includes(rule.uom)) return "fee_uom";
  if (!rate.ok) return rate.error === "negative" ? "rate_negative" : "rate_invalid";
  if (!isNewInputRateAmount(rate.value)) return "rate_not_new_input";
  if (compareDecimal(rate.value, SWITCH_RATE_CAPS[`${rule.fee_type}|${rule.uom}`]) > 0) return "rate_over_cap";
  if (rule.is_tou) {
    if (!HHMM.test(rule.start_time ?? "") || !HHMM.test(rule.end_time ?? "")) return "window_invalid";
    // A window ending before it starts runs past midnight; only a zero-length one is empty.
    if (rule.start_time === rule.end_time) return "window_empty";
    if (!DAYS.test(rule.days ?? "")) return "days_invalid";
    if (!Number.isSafeInteger(rule.priority ?? 0)) return "priority_invalid";
  }
  return null;
}

/** The problems of one entry's rules, as the shared editor validates them plus the new-input limits and caps. */
function entryProblems(entry: SwitchDraftEntry, ref: SwitchItemRef, paid: boolean): SwitchDraftProblem[] {
  if (entry.status === "not_entered") return [{ kind: "item_not_entered", item: ref }];
  const problems: SwitchDraftProblem[] = entry.status === "changed" ? [{ kind: "item_changed", item: ref }] : [];
  const allDay = new Set<string>();
  let positive = false;
  entry.values.rules.forEach((rule, index) => {
    const rate = ruleRate(rule);
    let problem = ruleProblem(rule, rate);
    const key = `${rule.fee_type}|${rule.uom}`;
    if (problem === null && !rule.is_tou) {
      // Two all-day rules for one fee and unit would both always apply.
      if (allDay.has(key)) problem = "all_day_conflict";
      allDay.add(key);
    }
    if (problem !== null) problems.push({ kind: "rule_invalid", item: ref, rule: index, problem });
    else if (rate.ok && compareDecimal(rate.value, "0") > 0) positive = true;
  });
  if (paid && !positive) problems.push({ kind: "paid_requires_positive_rate", item: ref });
  return problems;
}

/** Everything that keeps the draft from a request, apart from the confirmation; empty when ready to confirm. */
export function switchDraftProblems(draft: OperatingCurrencySwitchDraft): readonly SwitchDraftProblem[] {
  if (draft.target === null) return deepFreeze([{ kind: "target_missing" }]);
  const problems: SwitchDraftProblem[] = [];
  if (draft.scope !== null) {
    for (const path of CONSUMER_PATHS) {
      const { ref, recorded_at } = draft.scope.evidence[path];
      if ((ref.trim() === "") !== (recorded_at.trim() === "")) problems.push({ kind: "evidence_incomplete", path });
      if (ref.includes("\0")) problems.push({ kind: "evidence_nul_character", path, field: "ref" });
      if (recorded_at.includes("\0")) problems.push({ kind: "evidence_nul_character", path, field: "recorded_at" });
    }
  }
  for (const { item, entry } of draft.units) problems.push(...entryProblems(entry, unitRef(item), item.pricing_mode === "paid"));
  for (const { item, entry } of draft.profiles) problems.push(...entryProblems(entry, profileRef(item), false));
  return deepFreeze(problems);
}

function replacementRule(rule: PricingRuleValues): SwitchReplacementRule {
  const rate = ruleRate(rule);
  if (!rate.ok) throw new Error("a validated rule has no writable rate");
  const window = rule.is_tou
    ? { is_tou: true, start_time: rule.start_time ?? "", end_time: rule.end_time ?? "", days: rule.days ?? "", priority: rule.priority ?? 0 }
    : { is_tou: false, start_time: "", end_time: "", days: "", priority: 0 };
  return { fee_type: rule.fee_type, uom: rule.uom, rate_base: rate.value, ...window, precision_origin: "new_input" } as SwitchReplacementRule;
}

function replacementPricebook(entry: SwitchDraftEntry, target: string, registryVersion: number): SwitchReplacementPricebook {
  if (entry.values === null) throw new Error("a validated item has no entry");
  return { money_version: 2, currency_code: target, registry_version: registryVersion, effective_pricebook: entry.values.rules.map(replacementRule) };
}

function activationScope(scope: SwitchScopeDraft): SwitchActivationScope {
  const evidence: Partial<Record<ConsumerPath, SwitchEvidenceRef>> = {};
  for (const path of CONSUMER_PATHS) {
    const { ref, recorded_at } = scope.evidence[path];
    if (ref.trim() !== "" && recorded_at.trim() !== "") evidence[path] = { ref: ref.trim(), recorded_at: recorded_at.trim() };
  }
  const consumer_states = Object.fromEntries(CONSUMER_PATHS.map((p) => [p, scope.consumer_states[p]])) as Record<ConsumerPath, ConsumerState>;
  return { consumer_states, evidence, suspended: scope.suspended };
}

/**
 * The exact activation body of a ready, confirmed draft: the expected currency and
 * revisions of its basis, every item in basis order with the version it was prepared
 * against, and each replacement as complete new input in the target with the basis
 * registry_version. Otherwise every problem, never a partial request.
 */
export function buildSwitchRequest(draft: OperatingCurrencySwitchDraft): SwitchBuildResult {
  const errors: SwitchBuildError[] = [...switchDraftProblems(draft)];
  if (!draft.confirmed) errors.push({ kind: "not_confirmed" });
  const target = draft.target;
  if (errors.length > 0 || target === null) return Object.freeze({ ok: false as const, errors: deepFreeze(errors) });
  const { basis } = draft;
  const request: OperatingCurrencySwitchRequest = {
    target_currency: target,
    expected_operating_currency: basis.operating_currency,
    expected_operating_currency_revision: basis.operating_currency_revision,
    expected_activation_scope_revision: basis.activation_scope_revision,
    activation_scope: draft.scope === null ? null : activationScope(draft.scope),
    units: draft.units.map(({ item, entry }) => ({ unit_id: item.unit_id, expected_pricebook_version: item.pricebook_version, pricebook: replacementPricebook(entry, target, basis.registry_version) })),
    profiles: draft.profiles.map(({ item, entry }) => ({ profile_id: item.profile_id, expected_revision: item.revision, pricebook: replacementPricebook(entry, target, basis.registry_version) })),
  };
  return Object.freeze({ ok: true as const, request: deepFreeze(request) });
}

/** The closed repair body for a committed revision. */
export function buildSwitchRepairRequest(revision: number): { readonly operating_currency_revision: number } {
  if (!Number.isSafeInteger(revision) || revision < 1) throw new RangeError(`repair revision ${revision}`);
  return Object.freeze({ operating_currency_revision: revision });
}

/** The identities a rebase added, removed or found changed, and whether the target had to be cleared. */
export interface SwitchRebaseSummary {
  readonly added: readonly SwitchItemRef[];
  readonly removed: readonly SwitchItemRef[];
  readonly changed: readonly SwitchItemRef[];
  readonly target_cleared: boolean;
}

export type SwitchRebaseResult =
  | { readonly ok: true; readonly draft: OperatingCurrencySwitchDraft; readonly summary: SwitchRebaseSummary }
  | { readonly ok: false; readonly error: "organization_mismatch" | "older_basis" };

function rebaseSlots<B, S extends { item: B; entry: SwitchDraftEntry }>(
  previous: readonly S[], next: readonly B[], id: (item: B) => string | number, same: (a: B, b: B) => boolean, ref: (item: B) => SwitchItemRef, keep: boolean,
  summary: { added: SwitchItemRef[]; removed: SwitchItemRef[]; changed: SwitchItemRef[] },
): { item: B; entry: SwitchDraftEntry }[] {
  const old = new Map(previous.map((s) => [id(s.item), s]));
  const nextIds = new Set(next.map(id));
  for (const s of previous) if (!nextIds.has(id(s.item))) summary.removed.push(ref(s.item));
  return next.map((item) => {
    const prior = old.get(id(item));
    if (prior === undefined) {
      summary.added.push(ref(item));
      return { item, entry: NOT_ENTERED };
    }
    const unchanged = same(prior.item, item);
    if (!unchanged) summary.changed.push(ref(item));
    if (!keep || prior.entry.status === "not_entered") return { item, entry: NOT_ENTERED };
    return { item, entry: unchanged ? prior.entry : { ...prior.entry, status: "changed" } };
  });
}

/**
 * Moves a draft onto a newer basis of the same organization. An eligible target and its
 * scope input stay; an item with the same identity and version keeps its entry, a changed
 * one keeps its typed values but needs explicit re-entry, a removed one is dropped and a
 * new one starts not entered. A target that is no longer eligible is cleared with its
 * scope and every entry. The confirmation always resets.
 */
export function rebaseSwitchDraft(draft: OperatingCurrencySwitchDraft, basis: OperatingCurrencySwitchBasis): SwitchRebaseResult {
  if (basis.organization_id !== draft.basis.organization_id) return Object.freeze({ ok: false as const, error: "organization_mismatch" as const });
  if (basis.operating_currency_revision < draft.basis.operating_currency_revision) return Object.freeze({ ok: false as const, error: "older_basis" as const });
  const keep = draft.target !== null && eligibleTarget(basis, draft.target) !== undefined;
  const summary = { added: [] as SwitchItemRef[], removed: [] as SwitchItemRef[], changed: [] as SwitchItemRef[] };
  const units = rebaseSlots(draft.units, basis.units, (u) => u.unit_id, (a, b) => a.pricebook_version === b.pricebook_version, unitRef, keep, summary);
  const profiles = rebaseSlots(draft.profiles, basis.profiles, (p) => p.profile_id, (a, b) => a.revision === b.revision && a.pricebook_version === b.pricebook_version, profileRef, keep, summary);
  const rebased: OperatingCurrencySwitchDraft = {
    basis,
    target: keep ? draft.target : null,
    scope: keep ? draft.scope : null,
    units,
    profiles,
    confirmed: false,
  };
  return Object.freeze({ ok: true as const, draft: deepFreeze(rebased), summary: deepFreeze({ ...summary, target_cleared: draft.target !== null && !keep }) });
}

// ── Unknown activation outcome and repair evidence ───────────────────────────────

/**
 * What one fresh prepare read after an unknown activation proves. committed: the target
 * and revision expected + 1 are stored, so a conservative repair of that revision is
 * due (never another activation). not_committed: currency and revision are unchanged;
 * the same draft may be submitted again, explicitly. changed: anything else; rebase.
 */
export type SwitchUnknownResolution =
  | { readonly outcome: "committed"; readonly operating_currency: string; readonly operating_currency_revision: number; readonly repair: { readonly operating_currency_revision: number } }
  | { readonly outcome: "not_committed" }
  | { readonly outcome: "changed" };

export function resolveUnknownSwitchActivation(organizationId: number, request: OperatingCurrencySwitchRequest, fresh: OperatingCurrencySwitchBasis): SwitchUnknownResolution {
  if (fresh.organization_id !== organizationId) return Object.freeze({ outcome: "changed" as const });
  const committedRevision = request.expected_operating_currency_revision + 1;
  if (fresh.operating_currency === request.target_currency && fresh.operating_currency_revision === committedRevision)
    return deepFreeze({ outcome: "committed" as const, operating_currency: fresh.operating_currency, operating_currency_revision: committedRevision, repair: buildSwitchRepairRequest(committedRevision) });
  if (fresh.operating_currency === request.expected_operating_currency && fresh.operating_currency_revision === request.expected_operating_currency_revision)
    return Object.freeze({ outcome: "not_committed" as const });
  return Object.freeze({ outcome: "changed" as const });
}

/**
 * The in-memory evidence that a committed switch revision of one origin
 * (`${account}:${orgId}`) still needs its post-commit effects resent (section 4.4 of the
 * S4 preflight). It exists only after one of these answers for that origin, never from a
 * revision alone and never after a reload.
 */
export interface SwitchRepairEvidence {
  readonly origin: string;
  readonly operating_currency_revision: number;
  readonly source: "committed_incomplete" | "repair_incomplete" | "unknown_committed";
}

export type SwitchRepairEvent =
  | { readonly kind: "committed"; readonly committed: OperatingCurrencySwitchCommitted }
  | { readonly kind: "repaired"; readonly repaired: OperatingCurrencySwitchRepaired }
  | { readonly kind: "unknown_resolved"; readonly resolution: SwitchUnknownResolution }
  /** `operating_currency_revision` is the revision the refused repair requested (its mutation variables). */
  | { readonly kind: "repair_stale"; readonly operating_currency_revision: number };

const evidence = (origin: string, operating_currency_revision: number, source: SwitchRepairEvidence["source"]): SwitchRepairEvidence =>
  Object.freeze({ origin, operating_currency_revision, source });

/**
 * The evidence of `origin` after one of its answers. `current` belongs to the same origin;
 * a value of another origin is returned unchanged, so a late answer never touches it.
 * An answer about an older revision than the evidence is late and leaves it unchanged;
 * a stale repair clears only the evidence of the revision it requested.
 */
export function nextSwitchRepairEvidence(current: SwitchRepairEvidence | null, origin: string, event: SwitchRepairEvent): SwitchRepairEvidence | null {
  if (current !== null && current.origin !== origin) return current;
  const older = (revision: number) => current !== null && current.operating_currency_revision > revision;
  switch (event.kind) {
    case "committed": {
      const { projection, operating_currency_revision } = event.committed;
      if (older(operating_currency_revision)) return current;
      return projection.status === "incomplete" ? evidence(origin, operating_currency_revision, "committed_incomplete") : null;
    }
    case "repaired": {
      const { projection, operating_currency_revision } = event.repaired;
      if (older(operating_currency_revision)) return current;
      return projection.status === "incomplete" ? evidence(origin, operating_currency_revision, "repair_incomplete") : null;
    }
    case "unknown_resolved": {
      const { resolution } = event;
      if (resolution.outcome !== "committed" || older(resolution.operating_currency_revision)) return current;
      return evidence(origin, resolution.operating_currency_revision, "unknown_committed");
    }
    case "repair_stale":
      return current !== null && current.operating_currency_revision === event.operating_currency_revision ? null : current;
  }
}

/** The repair body to offer for `origin`, or null: only on its own evidence. */
export function switchRepairOffer(current: SwitchRepairEvidence | null, origin: string): { readonly operating_currency_revision: number } | null {
  return current !== null && current.origin === origin ? buildSwitchRepairRequest(current.operating_currency_revision) : null;
}
